/**
===================================================
Module: frontend.src.components.VideoPreview.tsx
Description: Video preview and player component with custom controls
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import "aframe";
import "../styles/VideoPreview.css";

interface VideoPreviewProps {
  file?: File;
  srcURL?: string;
  startTime?: number;
  endTime?: number;
  playing?: boolean;
  loop?: boolean;
  onLoadedMetadata?: (duration: number, video: HTMLVideoElement) => void;
  onTimeUpdate?: (time: number) => void;
  onError?: (error: any) => void;
  className?: string;
  controls?: boolean;
  enable360?: boolean;
}

// Add these SVG icon components at the top of the file
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const VolumeMutedIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const VolumeHighIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const VolumeMediumIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
  </svg>
);

const FullscreenIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

export const VideoPreview = forwardRef<HTMLVideoElement, VideoPreviewProps>(
  (
    {
      file,
      srcURL,
      startTime = 0,
      endTime,
      playing = false,
      loop = false,
      onLoadedMetadata,
      onTimeUpdate,
      onError,
      className,
      controls = false,
      enable360 = false,
    },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const aframeVideoRef = useRef<HTMLVideoElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // A-Frame video player state
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

    const objectURL = React.useMemo(() => {
      return file ? URL.createObjectURL(file) : null;
    }, [file]);

    // Format time helper
    const formatTime = (seconds: number): string => {
      if (isNaN(seconds) || !isFinite(seconds)) return "0:00";

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    // Video control functions
    const togglePlayPause = () => {
      const video = document.getElementById("video360") as HTMLVideoElement;
      if (!video) {
        return;
      }

      if (video.paused) {
        video
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {});
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Setup A-Frame video event listeners
    useEffect(() => {
      if (!enable360) return;

      const setupVideoListeners = () => {
        const video = document.getElementById("video360") as HTMLVideoElement;
        if (!video) {
          return null;
        }

        aframeVideoRef.current = video;

        const handleLoadedMetadata = () => {
          setDuration(video.duration);
          setIsLoading(false);
          if (onLoadedMetadata) {
            onLoadedMetadata(video.duration, video);
          }
          if (startTime > 0) {
            video.currentTime = startTime;
          }
        };

        const handleTimeUpdate = () => {
          setCurrentTime(video.currentTime);
          if (onTimeUpdate) {
            onTimeUpdate(Math.floor(video.currentTime));
          }

          // Handle end time for trimming
          if (typeof endTime === "number" && video.currentTime >= endTime) {
            if (loop) {
              video.currentTime = startTime;
              video.play();
            } else {
              video.pause();
              video.currentTime = startTime;
            }
          }
        };

        const handlePlay = () => {
          setIsPlaying(true);
        };

        const handlePause = () => {
          setIsPlaying(false);
        };

        const handleLoadStart = () => {
          setIsLoading(true);
        };

        const handleCanPlay = () => {
          setIsLoading(false);
        };

        const handleVolumeChange = () => {
          setVolume(video.volume);
          setIsMuted(video.muted);
        };

        // Remove any existing listeners first
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("loadstart", handleLoadStart);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("volumechange", handleVolumeChange);

        // Add new listeners
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("loadstart", handleLoadStart);
        video.addEventListener("canplay", handleCanPlay);
        video.addEventListener("volumechange", handleVolumeChange);

        return () => {
          video.removeEventListener("loadedmetadata", handleLoadedMetadata);
          video.removeEventListener("timeupdate", handleTimeUpdate);
          video.removeEventListener("play", handlePlay);
          video.removeEventListener("pause", handlePause);
          video.removeEventListener("loadstart", handleLoadStart);
          video.removeEventListener("canplay", handleCanPlay);
          video.removeEventListener("volumechange", handleVolumeChange);
        };
      };

      // Try to setup listeners immediately
      const cleanup = setupVideoListeners();
      if (cleanup) {
        return cleanup;
      }

      // If video not ready, wait for it
      const interval = setInterval(() => {
        const cleanup = setupVideoListeners();
        if (cleanup) {
          clearInterval(interval);
          return cleanup;
        }
      }, 200);

      return () => {
        clearInterval(interval);
      };
    }, [enable360, onLoadedMetadata, onTimeUpdate, startTime, endTime, loop]);

    // Handle external playing prop - simplified
    useEffect(() => {
      if (!enable360) return;

      const video = document.getElementById("video360") as HTMLVideoElement;
      if (!video) return;

      if (playing && video.paused) {
        video.play().catch(console.error);
      } else if (!playing && !video.paused) {
        video.pause();
      }
    }, [playing, enable360]);

    const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!aframeVideoRef.current || !duration) return;

      const newTime = (parseFloat(e.target.value) / 100) * duration;
      aframeVideoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!aframeVideoRef.current) return;

      const newVolume = parseFloat(e.target.value) / 100;
      aframeVideoRef.current.volume = newVolume;
      setVolume(newVolume);
    };

    const toggleMute = () => {
      if (!aframeVideoRef.current) return;

      aframeVideoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    };

    const toggleFullscreen = () => {
      if (!wrapperRef.current) return;

      if (!isFullscreen) {
        // Cross-browser fullscreen request
        const wrapper = wrapperRef.current;
        if (wrapper.requestFullscreen) {
          wrapper.requestFullscreen();
        } else if ((wrapper as any).webkitRequestFullscreen) {
          (wrapper as any).webkitRequestFullscreen();
        } else if ((wrapper as any).mozRequestFullScreen) {
          (wrapper as any).mozRequestFullScreen();
        } else if ((wrapper as any).msRequestFullscreen) {
          (wrapper as any).msRequestFullscreen();
        }
        // Immediately update state for responsive UI
        setIsFullscreen(true);
      } else {
        // Cross-browser fullscreen exit
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
        // Immediately update state for responsive UI
        setIsFullscreen(false);
      }
    };

    // Handle fullscreen change events - improved detection
    useEffect(() => {
      if (!enable360) return;

      const handleFullscreenChange = () => {
        // Cross-browser fullscreen element detection
        const fullscreenElement =
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement;

        const isCurrentlyFullscreen = !!fullscreenElement;

        // Check if our wrapper is the fullscreen element
        const isOurWrapperFullscreen = fullscreenElement === wrapperRef.current;

        setIsFullscreen(isCurrentlyFullscreen && isOurWrapperFullscreen);
      };

      // Listen for cross-browser fullscreen events
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);

      // Initial check for fullscreen state
      handleFullscreenChange();

      return () => {
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullscreenChange,
        );
        document.removeEventListener(
          "MSFullscreenChange",
          handleFullscreenChange,
        );
      };
    }, [enable360]);

    // Handle video events for regular video only
    useEffect(() => {
      if (enable360 || !videoRef.current) return;

      const video = videoRef.current;

      const handleLoadedMetadata = () => {
        if (onLoadedMetadata) {
          onLoadedMetadata(video.duration, video);
        }
        if (startTime > 0) {
          video.currentTime = startTime;
        }
      };

      const handleTimeUpdate = () => {
        if (onTimeUpdate) {
          onTimeUpdate(Math.floor(video.currentTime));
        }

        if (typeof endTime === "number" && video.currentTime >= endTime) {
          if (loop) {
            video.currentTime = startTime;
            video.play();
          } else {
            video.pause();
            video.currentTime = startTime;
          }
        }
      };

      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("timeupdate", handleTimeUpdate);

      return () => {
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("timeupdate", handleTimeUpdate);
      };
    }, [enable360, onLoadedMetadata, onTimeUpdate, startTime, endTime, loop]);

    // Handle play/pause for regular video only
    useEffect(() => {
      if (enable360 || !videoRef.current) return;

      if (playing) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }, [playing, enable360]);

    // Handle start time changes for regular video only
    useEffect(() => {
      if (enable360 || !videoRef.current) return;
      videoRef.current.currentTime = Math.floor(startTime);
    }, [startTime, enable360]);

    // Handle video error events
    useEffect(() => {
      if (!enable360 || !videoRef.current) return;

      const video = videoRef.current;

      const handleError = (event: Event) => {
        const error = {
          status: 0,
          message: "Video failed to load",
          event,
        };

        // Try to get more specific error information
        if (video.error) {
          switch (video.error.code) {
            case MediaError.MEDIA_ERR_NETWORK:
              error.message = "Network error occurred while loading video";
              error.status = 403; // Treat network errors as potential URL expiration
              break;
            case MediaError.MEDIA_ERR_DECODE:
              error.message = "Video decoding error";
              break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              error.message = "Video source not supported";
              error.status = 403; // Treat unsupported source as potential URL expiration
              break;
            default:
              error.message = "Unknown video error";
          }
        }

        if (onError) {
          onError(error);
        }
      };

      video.addEventListener("error", handleError);

      return () => {
        video.removeEventListener("error", handleError);
      };
    }, [enable360, onError]);

    // Handle A-Frame video errors
    useEffect(() => {
      if (!enable360) return;

      const setupVideoErrorHandling = () => {
        const video = document.getElementById("video360") as HTMLVideoElement;
        if (!video) {
          return null;
        }

        const handleError = (event: Event) => {
          const error = {
            status: 0,
            message: "A-Frame video failed to load",
            event,
          };

          // Try to get more specific error information
          if (video.error) {
            switch (video.error.code) {
              case MediaError.MEDIA_ERR_NETWORK:
                error.message =
                  "Network error occurred while loading A-Frame video";
                error.status = 403; // Treat network errors as potential URL expiration
                break;
              case MediaError.MEDIA_ERR_DECODE:
                error.message = "A-Frame video decoding error";
                break;
              case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                error.message = "A-Frame video source not supported";
                error.status = 403; // Treat unsupported source as potential URL expiration
                break;
              default:
                error.message = "Unknown A-Frame video error";
            }
          }

          if (onError) {
            onError(error);
          }
        };

        video.addEventListener("error", handleError);

        return () => {
          video.removeEventListener("error", handleError);
        };
      };

      const cleanup = setupVideoErrorHandling();
      if (cleanup) {
        return cleanup;
      }

      // If video not ready, wait for it
      const interval = setInterval(() => {
        const cleanup = setupVideoErrorHandling();
        if (cleanup) {
          clearInterval(interval);
          return cleanup;
        }
      }, 200);

      return () => {
        clearInterval(interval);
      };
    }, [enable360, onError]);

    const videoSrc = objectURL || srcURL;

    if (!videoSrc) {
      return (
        <div className="video-preview-container">
          <div>No video source provided</div>
        </div>
      );
    }

    // Use A-Frame when 360° is enabled with custom controls
    if (enable360) {
      return (
        <div className="video-preview-container">
          <div
            ref={wrapperRef}
            className={`aframe-wrapper ${isFullscreen ? "全画面表示" : ""}`}
          >
            <div className="aframe-container">
              <a-scene
                embedded
                style={{
                  width: "100%",
                  height: isFullscreen ? "100vh" : "400px",
                }}
                vr-mode-ui="enabled: false"
                background="color: #000"
              >
                <a-assets>
                  <video
                    id="video360"
                    src={videoSrc}
                    loop={loop}
                    crossOrigin="anonymous"
                    playsInline
                    preload="metadata"
                    controlsList="nodownload nofullscreen noremoteplayback"
                    disablePictureInPicture
                  />
                </a-assets>

                <a-videosphere
                  src="#video360"
                  rotation="0 180 0"
                  radius="500"
                  segments-width="64"
                  segments-height="32"
                />

                <a-camera
                  look-controls="enabled: true"
                  wasd-controls="enabled: false"
                  fov="80"
                  near="0.1"
                  far="10000"
                  position="0 0 0"
                />
              </a-scene>

              {isLoading && (
                <div className="aframe-loading-overlay">
                  <div className="loading-spinner"></div>
                  <span>動画のロード中...</span>
                </div>
              )}
            </div>

            {controls && !isLoading && (
              <div className="aframe-video-controls">
                <div className="controls-row">
                  <button
                    className="aframe-control-btn play-pause-btn"
                    onClick={togglePlayPause}
                    title={isPlaying ? "一時停止" : "再生"}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </button>

                  <div className="progress-container">
                    <input
                      type="range"
                      className="aframe-progress"
                      min="0"
                      max="100"
                      value={duration ? (currentTime / duration) * 100 : 0}
                      onChange={handleProgressChange}
                      title="シーク"
                    />
                  </div>

                  <span className="aframe-time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>

                  <div className="volume-container">
                    <button
                      className="aframe-control-btn volume-btn"
                      onClick={toggleMute}
                      title={isMuted ? "ミュート解除" : "ミュート"}
                    >
                      {isMuted ? (
                        <VolumeMutedIcon />
                      ) : volume > 0.5 ? (
                        <VolumeHighIcon />
                      ) : (
                        <VolumeMediumIcon />
                      )}
                    </button>
                    <input
                      type="range"
                      className="aframe-volume"
                      min="0"
                      max="100"
                      value={isMuted ? 0 : volume * 100}
                      onChange={handleVolumeChange}
                      title="音量"
                    />
                  </div>

                  <button
                    className="aframe-control-btn fullscreen-btn"
                    onClick={toggleFullscreen}
                    title="全画面表示"
                  >
                    <FullscreenIcon />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Standard HTML5 video for regular playback
    return (
      <div className="video-preview-container">
        <video
          ref={videoRef}
          className={className}
          controls={controls}
          loop={loop}
          src={videoSrc}
          style={{ width: "100%" }}
          onEnded={() => {
            if (loop && videoRef.current) {
              videoRef.current.currentTime = Math.floor(startTime);
              videoRef.current.play().catch(() => {});
            }
          }}
        />
      </div>
    );
  },
);
